import * as React from "react"
import { Button } from "../ui/button"
import { Typography } from "../ui/typography"
import { cn } from "../../lib/utils"
import { LoginHeroCarousel, LoginHeroSlide } from "./LoginHeroCarousel"

// Brand Icons
const GoogleIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M23.52 12.29C23.52 11.43 23.44 10.61 23.3 9.82H12V14.45H18.46C18.18 15.93 17.33 17.18 16.07 18.01V20.97H19.94C22.2 18.89 23.52 15.82 23.52 12.29Z" fill="#4285F4" />
        <path d="M12 24C15.24 24 17.96 22.92 19.94 21.09L16.07 18.13C15 18.85 13.62 19.27 12 19.27C8.88 19.27 6.23 17.17 5.29 14.35H1.28V17.46C3.25 21.37 7.31 24 12 24Z" fill="#34A853" />
        <path d="M5.29 14.35C5.05 13.63 4.92 12.87 4.92 12.09C4.92 11.31 5.05 10.55 5.29 9.83V6.72H1.28C0.46 8.35 0 10.17 0 12.09C0 14.01 0.46 15.83 1.28 17.46L5.29 14.35Z" fill="#FBBC05" />
        <path d="M12 4.91C13.76 4.91 15.34 5.52 16.58 6.7L20.03 3.25C17.96 1.32 15.24 0 12 0C7.31 0 3.25 2.63 1.28 6.54L5.29 9.65C6.23 6.83 8.88 4.91 12 4.91Z" fill="#EA4335" />
    </svg>
)

const FacebookIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 17.9895 4.38823 22.954 10.125 23.8542V15.4688H7.07813V12H10.125V9.35625C10.125 6.34875 11.9165 4.6875 14.6576 4.6875C15.9705 4.6875 17.3438 4.92188 17.3438 4.92188V7.875H15.8306C14.34 7.875 13.875 8.8 13.875 9.75V12H17.2031L16.6711 15.4688H13.875V23.8542C19.6118 22.954 24 17.9895 24 12Z" fill="#1877F2" />
    </svg>
)

const TikTokIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 0C5.37 0 0 5.37 0 12C0 18.63 5.37 24 12 24C18.63 24 24 18.63 24 12C24 5.37 18.63 0 12 0ZM16.69 8.83C16.69 8.83 16.69 8.82 16.69 8.83C15.76 8.83 14.88 8.53 14.16 8.02C13.44 7.51 12.91 6.8 12.63 5.98V14.62C12.63 16.7 10.94 18.39 8.86 18.39C6.78 18.39 5.09 16.7 5.09 14.62C5.09 12.54 6.78 10.85 8.86 10.85C9.07 10.85 9.28 10.87 9.48 10.91V13.56C9.28 13.52 9.07 13.5 8.86 13.5C8.24 13.5 7.74 14 7.74 14.62C7.74 15.24 8.24 15.74 8.86 15.74C9.48 15.74 9.98 15.24 9.98 14.62V2.66H12.63C12.63 4.64 14.24 6.25 16.22 6.25V8.83H16.69Z" fill="black" />
    </svg>
)

const XIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18.901 0H22.581L14.542 9.189L24 21.691H16.596L10.796 14.108L4.156 21.691H0.474L9.049 11.89L0 0H7.594L12.862 6.965L18.901 0ZM17.61 19.489H19.649L6.486 2.086H4.298L17.61 19.489Z" fill="black" />
    </svg>
)

export interface LoginScreenProps extends React.HTMLAttributes<HTMLDivElement> {
    onGoogleClick?: () => void
    onFacebookClick?: () => void
    onTikTokClick?: () => void
    onXClick?: () => void
    onLinkedInClick?: () => void
}

export const LoginScreen = React.forwardRef<HTMLDivElement, LoginScreenProps>(
    ({ className, onGoogleClick, onFacebookClick, onTikTokClick, onXClick, onLinkedInClick, ...props }, ref) => {
        const heroSlides = Array.from({ length: 3 }, (_, index) => (
            <LoginHeroSlide key={`login-hero-slide-${index}`} label="Image Placeholder" />
        ))

        return (
            <div
                ref={ref}
                className={cn("flex flex-col min-h-[100dvh] w-full bg-white", className)}
                {...props}
            >
                {/* Top Placeholder Image Area */}
                <LoginHeroCarousel className="min-h-[45vh] h-auto" slides={heroSlides} />

                {/* Content Area */}
                <div className="flex-1 flex flex-col px-8 pt-8 pb-8 space-y-6">
                    <div className="space-y-2">
                        <Typography variant="h2" className="text-3xl font-bold text-left tracking-tight">
                            Sign in to continue
                        </Typography>
                        <Typography variant="muted" className="text-base">
                            Welcome back! Please enter your details.
                        </Typography>
                    </div>

                    {/* Social Icons Row */}
                    <div className="grid grid-cols-4 gap-4 pt-4">
                        <Button
                            variant="outline"
                            className="h-14 border-gray-200 rounded-2xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
                            onClick={onGoogleClick}
                        >
                            <GoogleIcon />
                        </Button>
                        <Button
                            variant="outline"
                            className="h-14 border-gray-200 rounded-2xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
                            onClick={onFacebookClick}
                        >
                            <FacebookIcon />
                        </Button>
                        <Button
                            variant="outline"
                            className="h-14 border-gray-200 rounded-2xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
                            onClick={onTikTokClick}
                        >
                            <TikTokIcon />
                        </Button>
                        <Button
                            variant="outline"
                            className="h-14 border-gray-200 rounded-2xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
                            onClick={onXClick}
                        >
                            <XIcon />
                        </Button>
                    </div>

                    {/* Spacer to push LinkedIn button to bottom */}
                    <div className="flex-1" />

                    {/* LinkedIn Button */}
                    <div className="pt-4 pb-safe">
                        <Button
                            className="w-full h-14 text-lg font-semibold rounded-2xl gap-3 shadow-lg shadow-blue-900/10 hover:shadow-blue-900/20 hover:opacity-95 transition-all active:scale-[0.98]"
                            style={{ backgroundColor: '#0A66C2', color: 'white' }}
                            onClick={onLinkedInClick}
                        >
                            <span>Continue with Linkedin</span>
                            <div className="bg-white/20 rounded p-0.5 ml-1">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M20.447 20.452H16.892V14.881C16.892 13.553 16.866 11.844 15.04 11.844C13.188 11.844 12.905 13.287 12.905 14.786V20.452H9.35V9H12.761V10.564H12.809C13.284 9.664 14.444 8.715 16.173 8.715C19.773 8.715 20.447 11.085 20.447 14.168V20.452Z" fill="white" />
                                    <path d="M5.337 7.433C4.197 7.433 3.274 6.509 3.274 5.369C3.274 4.229 4.197 3.305 5.337 3.305C6.477 3.305 7.4 4.229 7.4 5.369C7.4 6.509 6.477 7.433 5.337 7.433Z" fill="white" />
                                    <path d="M7.119 20.452H3.556V9H7.119V20.452Z" fill="white" />
                                </svg>
                            </div>
                        </Button>
                    </div>
                </div>
            </div>
        )
    }
)

LoginScreen.displayName = "LoginScreen"
